import _ from "lodash";
import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useSocket } from "Context/SocketContext";
import { useSelector } from "react-redux";
import { RootState } from "../../../store"
import socketInstance from "config/socket";

export const ChatContext = createContext<ContextType | null>(null);

export const useChatContext = (): ContextType => {
    const context = useContext(ChatContext);
    if (!context) {
        throw new Error("useChatContext must be used within a ChatContextProvider");
    }
    return context;
}


const ChatContextProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const socket: typeof socketInstance | null = useSocket();
    const { isSocketConnected } = useSelector((state: RootState) => state.auth)

    const [chatRoom, setChatRoom] = useState<Partial<ChatThreadType>>({})

    const INITIAL_CHAT_THREAD: ChatThreadListingType = useMemo(() => {
        return {
            isError: false,
            isLoading: true,
            data: []
        }
    }, [])


    const [chatThread, setChatThread] = useState<ChatThreadListingType>(INITIAL_CHAT_THREAD)

    useEffect(() => {
        if (isSocketConnected) {
            socket?.on('getChatThreads_', (data: SocketResponseInterface<ChatThreadType[], unknown>) => {
                setChatThread(prev => {
                    return {
                        ...prev,
                        isLoading: false,
                        data: data?.data || []
                    }
                })
            })

            socket?.on('newThread_', (data: SocketResponseInterface<ChatThreadType, unknown>) => {
                socket?.emit('_joinRoom', data?.data?.chat_room_id)
                socket?.emit('_getChatThreads')
            })


        }


        return () => {
            if (isSocketConnected) {
                socket?.dispose('getChatThreads_')
                socket?.dispose('newThread_')
            }
        }

    }, [isSocketConnected])


    useEffect(() => {
        if (isSocketConnected) {
            socket?.on('receivedMessage_', (res: SocketResponseInterface<ChatMessageType, unknown>) => {
                const data = res?.data || {};
                setChatThread(prev => {
                    return {
                        ...prev,
                        data: prev?.data.map(item => {
                            if (item.chat_room_id !== data?.chat_room_id) return item
                            return {
                                ...item,
                                message: {
                                    ...item?.message,
                                    message_type: data?.message_type,
                                    message: data?.message,
                                    createdAt: data?.message_timestamp,
                                },
                                unread_message_count: (chatRoom?.chat_room_id === data?.chat_room_id) ? item?.unread_message_count : (item?.unread_message_count + 1)
                            }
                        })
                    }
                })
            })

        }


        return () => {
            if (isSocketConnected) {
                socket?.dispose('receivedMessage_')
            }
        }

    }, [isSocketConnected, chatRoom])


    useEffect(() => {
        if (!isSocketConnected) return
        socket?.emit('_getChatThreads')
    }, [isSocketConnected])


    const updateLastMessagePayload = (data: ChatMessageType) => {
        setChatThread((prev) => ({
            ...prev,
            data: prev.data.map((item) =>
                item.chat_room_id !== data.chat_room_id
                    ? item
                    : {
                        ...item,
                        message: {
                            ...item?.message,
                            message_type: data?.message_type || "",
                            message: data?.message || "",
                            createdAt: data?.createdAt || "",
                            chat_room_id: data.chat_room_id
                        },
                    }
            ),
        }));
    };


    const resetMessageCount = (chat_room_id: number) => {
        setChatThread((prev) => ({
            ...prev,
            data: prev.data.map((item) =>
                item.chat_room_id !== chat_room_id
                    ? item
                    : { ...item, unread_message_count: 0 }
            ),
        }));
    };

    const findChatRoom = (chat_room_id: number) => {
        const record = chatThread?.data?.find(
            (item) => item.chat_room_id === chat_room_id
        );

        if (_.isEmpty(record)) {
            socket?.emit('_getChatThreads')
        }
    };


    return (
        <ChatContext.Provider value={{
            chatThread,
            chatRoom,
            setChatRoom,
            updateLastMessagePayload,
            resetMessageCount,
            findChatRoom
        }}>
            {children}
        </ChatContext.Provider>
    )

}

export default ChatContextProvider